import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Home, Shield, Mail, Phone, MapPin, Lock, Eye, Database, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import Since1998Badge from "@/components/Since1998Badge";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Privacy Policy | Its-Sold.com</title>
        <meta name="description" content="Its-Sold.com privacy policy. How we protect your data, anti-solicitation guarantees, and your rights as a FSBO seller in DFW." />
        <link rel="canonical" href="https://its-sold.com/privacy-policy" />
      </Helmet>
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>
                Its-Sold.com<sup className="text-xs">™</sup>
              </span>
            </div>
            <Since1998Badge variant="header" />
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-10 h-10 text-orange-600" />
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-gray-500">Last Updated: February 2026</p>
            </div>
          </div>

          <div className="prose max-w-none space-y-8">
            
            {/* Introduction */}
            <section>
              <p className="text-gray-700 text-lg">
                Net Auctions, Inc. (DBA Its-Sold.com) is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you visit our website 
                its-sold.com or use our services.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Database className="w-6 h-6 text-orange-600" />
                Information We Collect
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Personal Information</h3>
              <p className="text-gray-700 mb-3">When you register or use our services, we may collect:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Name and email address</li>
                <li>Phone number (optional, for SMS notifications)</li>
                <li>Property address and listing details</li>
                <li>Payment information (processed securely by third-party processors)</li>
                <li>Real estate license information (for realtors)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Automatically Collected Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>IP address and browser type</li>
                <li>Device information</li>
                <li>Pages visited and time spent on site</li>
                <li>Referring website</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="w-6 h-6 text-orange-600" />
                How We Use Your Information
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>To provide and maintain our FSBO listing services</li>
                <li>To process transactions and send related information</li>
                <li>To send administrative information (account updates, security alerts)</li>
                <li>To send promotional communications (only with your consent)</li>
                <li>To respond to inquiries and provide customer support</li>
                <li>To improve our website and services</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <UserCheck className="w-6 h-6 text-orange-600" />
                Information Sharing & Disclosure
              </h2>
              
              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-5 mb-4">
                <p className="font-bold text-green-900 text-lg mb-2">We Do NOT Sell Your Personal Information</p>
                <p className="text-gray-700">
                  Its-Sold.com will never sell, rent, or trade your personal information to third parties 
                  for their marketing purposes.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">We May Share Information With:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Service Providers:</strong> Companies that help us operate our website (hosting, analytics, payment processing)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> For any other purpose with your explicit permission</li>
              </ul>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                <p className="text-gray-700">
                  <strong>Important:</strong> Your phone number is NEVER shared with realtors or third parties. 
                  All SMS communications are sent directly by Its-Sold.com. See our 
                  <a href="/sms-terms" className="text-blue-600 hover:underline ml-1">SMS Terms</a> for details.
                </p>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="w-6 h-6 text-orange-600" />
                Data Security
              </h2>
              <p className="text-gray-700 mb-3">
                We implement appropriate technical and organizational security measures to protect your personal information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>SSL/TLS encryption for data transmission</li>
                <li>Secure password hashing</li>
                <li>Regular security assessments</li>
                <li>Limited employee access to personal data</li>
                <li>Two-factor authentication option</li>
              </ul>
              <p className="text-gray-600 mt-3 text-sm">
                While we strive to protect your information, no method of transmission over the Internet is 100% secure. 
                We cannot guarantee absolute security.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Privacy Rights</h2>
              <p className="text-gray-700 mb-3">You have the right to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Request your data in a portable format</li>
              </ul>
              <p className="text-gray-700 mt-3">
                To exercise these rights, contact us at <a href="mailto:privacy@its-sold.com" className="text-blue-600 hover:underline">privacy@its-sold.com</a>
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies & Tracking</h2>
              <p className="text-gray-700 mb-3">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Keep you logged in</li>
                <li>Remember your preferences</li>
                <li>Analyze website traffic (Google Analytics)</li>
                <li>Improve our services</li>
              </ul>
              <p className="text-gray-700 mt-3">
                You can control cookies through your browser settings. Disabling cookies may affect site functionality.
              </p>
            </section>

            {/* Third-Party Links */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
              <p className="text-gray-700">
                Our website may contain links to third-party websites. We are not responsible for the privacy 
                practices of these external sites. We encourage you to review their privacy policies.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Children&apos;s Privacy</h2>
              <p className="text-gray-700">
                Our services are not intended for individuals under 18 years of age. We do not knowingly collect 
                personal information from children. If you believe we have collected information from a minor, 
                please contact us immediately.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy periodically. We will notify you of significant changes by 
                posting the new policy on this page and updating the &quot;Last Updated&quot; date. Your continued use 
                of our services after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact */}
            <section className="border-t pt-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="font-semibold text-gray-900 mb-3">Net Auctions, Inc. (DBA Its-Sold.com)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div className="space-y-2">
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      3709 South Shadycreek Drive, Arlington, TX 76013
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      (817) 320-5334
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <a href="mailto:privacy@its-sold.com" className="text-blue-600 hover:underline">privacy@its-sold.com</a>
                    </p>
                    <p className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-gray-500" />
                      EIN: 41-3894967
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Policies */}
            <section className="border-t pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Related Policies</h2>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => navigate('/terms')}>
                  Terms of Service
                </Button>
                <Button variant="outline" onClick={() => navigate('/sms-terms')}>
                  SMS Terms & Conditions
                </Button>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t">
            <Button onClick={() => navigate('/')} variant="outline">
              Return to Homepage
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
